import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Card, Row, Col, ListGroup, Modal, InputGroup, Collapse, Tab, Tabs, Alert } from 'react-bootstrap';
import { ChevronDown, ChevronUp, Pencil, Trash, Plus, Tags, PiggyBank, CreditCard, ArrowRight, Building } from 'react-bootstrap-icons';
import ConfirmationModal from '../../shared/components/ConfirmationModal';
import { formatCurrency } from '../../shared/utils/currency';
import PaymentSourceManager from './components/PaymentSourceManager';
import IconPicker from '../../shared/components/IconPicker';
import { renderIcon } from '../../shared/utils/iconUtils'; // Import the shared icon utility
import AssetTypeManager from '../assets/AssetTypeManager'; // Corrected import
import { formatToLocalTime } from '../../shared/utils/dateUtils'; // Import formatToLocalTime
import './Settings.css';

// Helper function to check for budget overlap
const isBudgetOverlap = (newBudget, existingBudgets) => {
  const newCategory = newBudget.category;
  const newYear = newBudget.year;
  const newMonths = newBudget.months; // [] for "All Months"

  for (const existingBudget of existingBudgets) {
    const existingCategory = existingBudget.category;
    const existingYear = existingBudget.year;
    const existingMonths = existingBudget.months; // [] for "All Months"

    // Rule 1: Categories must match
    if (newCategory !== existingCategory) {
      continue; // No overlap if categories are different
    }

    // Rule 2: Check for year overlap
    if (newYear !== existingYear) {
      continue; // No overlap if years are different
    }

    // Rule 3: Check for month overlap
    // Case A: Both new and existing budgets are for "All Months" (months array is empty)
    if (newMonths.length === 0 && existingMonths.length === 0) {
      return true; // Overlap: (Category, Year, All Months) vs (Category, Year, All Months)
    }
    // Case B: One is "All Months" and the other has specific months
    if (newMonths.length === 0 || existingMonths.length === 0) {
      return true;
    }
    // Case C: Both have specific months, check for intersection
    const monthIntersection = newMonths.filter(month => existingMonths.includes(month));
    if (monthIntersection.length > 0) {
      return true; // Overlap: (Category, Year, [1,2]) vs (Category, Year, [2,3])
    }
  }
  return false; // No overlap found
};

const Settings = ({ settings, onSave, currency, profileId, profileType }) => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategoryInputs, setNewSubcategoryInputs] = useState({});
  const [subcategoryIcons, setSubcategoryIcons] = useState({});
  const [openSubcategories, setOpenSubcategories] = useState({});

  const [budgets, setBudgets] = useState([]);
  const [newBudgetCategory, setNewBudgetCategory] = useState('');
  const [newBudgetYear, setNewBudgetYear] = useState(new Date().getFullYear().toString());
  const [newBudgetMonth, setNewBudgetMonth] = useState([]);
  const [newBudgetAmount, setNewBudgetAmount] = useState('');
  const [editingBudgetIndex, setEditingBudgetIndex] = useState(null);
  const [selectedBudgetYearFilter, setSelectedBudgetYearFilter] = useState('All');
  const [sourceCopyYear, setSourceCopyYear] = useState(new Date().getFullYear().toString());
  const [targetCopyYear, setTargetCopyYear] = useState((new Date().getFullYear() + 1).toString());

  // The Source Year <select> only offers years that actually have a budget
  // (unlike Target Year, which always includes the current/next year too).
  // sourceCopyYear's initial value (today's year) has no guarantee of being
  // one of those options - e.g. if the only existing budget is for 2025 and
  // it's currently 2026. When that happens the browser silently falls back
  // to displaying the first real option, but the bound React state never
  // gets updated (no onChange fires), so a user who leaves Source Year
  // untouched - because it's already showing the right year - unknowingly
  // submits a stale value that can collide with whatever they pick for
  // Target Year, wrongly tripping the "cannot be the same" check. Keep it
  // snapped to a real option whenever the set of budgeted years changes.
  useEffect(() => {
    const existingBudgetYears = [...new Set(budgets.map(b => b.year))].sort((a, b) => a - b);
    if (existingBudgetYears.length === 0) return;
    const validYears = existingBudgetYears.map(String);
    setSourceCopyYear(prev => validYears.includes(prev)
      ? prev
      : existingBudgetYears[existingBudgetYears.length - 1].toString());
  }, [budgets]);

  const [currentCurrency, setCurrentCurrency] = useState(settings.currency || 'USD');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [currentCategoryEdit, setCurrentCategoryEdit] = useState({ index: null, name: '', subcategories: [], icon: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);
  const [settingsSubTabKey, setSettingsSubTabKey] = useState('categories');

  // Icon Picker States
  const [showIconPickerModal, setShowIconPickerModal] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('');
  const [contextForIconPicker, setContextForIconPicker] = useState({ type: null, index: null }); // 'newCategory' | 'editCategory'

  useEffect(() => {
    setCategories(settings.categories || []);
    setBudgets(settings.budgets || []);
    setCurrentCurrency(settings.currency || 'USD');
    const initialCollapseState = {};
    if (settings.categories) {
      settings.categories.forEach((_, index) => {
        initialCollapseState[index] = false;
      });
    }
    setOpenSubcategories(initialCollapseState);
  }, [settings]);

  const handleSave = () => {
    onSave({ categories, rules: settings.rules, budgets, currency: currentCurrency });
  };

  // Category Management Handlers
  const handleAddCategory = () => {
    if (newCategory && !categories.find(c => c.name === newCategory)) {
      const updatedCategories = [...categories, { name: newCategory, subcategories: [], icon: selectedIcon }];
      setCategories(updatedCategories);
      setNewCategory('');
      setSelectedIcon('');
    }
  };

  const handleDeleteCategoryClick = (index) => {
    const categoryToDelete = categories[index];
    setConfirmModalTitle('Confirm Category Deletion');
    setConfirmModalMessage(`Are you sure you want to delete the category "${categoryToDelete.name}"? All associated rules and budgets will also be removed. This action cannot be undone.`);
    setConfirmModalAction(() => () => _deleteCategory(index));
    setShowConfirmModal(true);
  };

  const _deleteCategory = (index) => {
    const categoryToDelete = categories[index];
    const updatedCategories = categories.filter((_, i) => i !== index);
    setCategories(updatedCategories);
    const updatedRules = settings.rules.filter(rule => rule.category !== categoryToDelete.name);
    const updatedBudgets = budgets.filter(budget => budget.category !== categoryToDelete.name);
    onSave({ categories: updatedCategories, rules: updatedRules, budgets: updatedBudgets });
    setShowConfirmModal(false);
  };

  const handleUpdateCategory = (index, newName, newIcon) => {
    const updatedCategories = categories.map((category, i) => {
      if (i === index) {
        return { ...category, name: newName, icon: newIcon };
      }
      return category;
    });
    
    setCategories(updatedCategories);
    
    // Auto-save the updated categories immediately
    onSave({ categories: updatedCategories, rules: settings.rules, budgets, currency: currentCurrency });
  };
  
  const handleAddSubcategory = (categoryIndex) => {
    const subcategoryName = newSubcategoryInputs[categoryIndex];
    if (subcategoryName) {
      const updatedCategories = [...categories];
      const existingSubcategories = updatedCategories[categoryIndex].subcategories || [];
      
      // Check if subcategory already exists (handle both string and object formats)
      const subcategoryExists = existingSubcategories.some(sub => 
        typeof sub === 'string' ? sub === subcategoryName : sub.name === subcategoryName
      );
      
      if (!subcategoryExists) {
        const newSubcategory = {
          name: subcategoryName,
          icon: subcategoryIcons[categoryIndex] || null
        };
        updatedCategories[categoryIndex].subcategories.push(newSubcategory);
        setCategories(updatedCategories);
        setNewSubcategoryInputs(prev => ({ ...prev, [categoryIndex]: '' }));
        setSubcategoryIcons(prev => ({ ...prev, [categoryIndex]: null }));
      }
    }
  };

  const handleDeleteSubcategoryClick = (categoryIndex, subcategoryIndex) => {
    const categoryName = categories[categoryIndex].name;
    const subcategoryToDelete = categories[categoryIndex].subcategories[subcategoryIndex];
    const subcategoryToDeleteName = typeof subcategoryToDelete === 'string' ? subcategoryToDelete : subcategoryToDelete.name;
    setConfirmModalTitle('Confirm Subcategory Deletion');
    setConfirmModalMessage(`Are you sure you want to delete the subcategory "${subcategoryToDeleteName}" from category "${categoryName}"? All associated rules will also be removed. This action cannot be undone.`);
    setConfirmModalAction(() => () => _deleteSubcategory(categoryIndex, subcategoryIndex));
    setShowConfirmModal(true);
  };

  const _deleteSubcategory = (categoryIndex, subcategoryIndex) => {
    const subcategoryToDelete = categories[categoryIndex].subcategories[subcategoryIndex];
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].subcategories = updatedCategories[categoryIndex].subcategories.filter((_, i) => i !== subcategoryIndex);
    setCategories(updatedCategories);
    setShowConfirmModal(false);
  };

  const handleEditCategoryClick = (index) => {
    setCurrentCategoryEdit({ index, ...categories[index] });
    setShowCategoryModal(true);
  };

  const handleSaveCategoryModal = () => {
    handleUpdateCategory(currentCategoryEdit.index, currentCategoryEdit.name, currentCategoryEdit.icon);
    setShowCategoryModal(false);
  };

  const toggleSubcategories = (index) => {
    setOpenSubcategories(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Budget Management Handlers
  const handleAddUpdateBudget = () => {
    if (!newBudgetCategory || !newBudgetYear || !newBudgetAmount || isNaN(parseFloat(newBudgetAmount)) || parseFloat(newBudgetAmount) <= 0) {
      alert('Please select a category, year, and enter a valid positive amount for the budget.');
      return;
    }

    const newBudget = {
      category: newBudgetCategory,
      amount: parseFloat(newBudgetAmount),
      year: parseInt(newBudgetYear),
      months: newBudgetMonth.length === 0 ? [] : newBudgetMonth.map(Number),
    };

    const budgetsForValidation = budgets.filter((_, i) => i !== editingBudgetIndex);

    if (isBudgetOverlap(newBudget, budgetsForValidation)) {
      alert('Budget overlap detected! Please ensure no two budgets for the same category cover the same time frame.');
      return;
    }

    let updatedBudgets;
    if (editingBudgetIndex !== null) {
      updatedBudgets = budgets.map((budget, index) =>
        index === editingBudgetIndex ? newBudget : budget
      );
      setEditingBudgetIndex(null);
    } else {
      updatedBudgets = [...budgets, newBudget];
    }
    setBudgets(updatedBudgets);
    setNewBudgetCategory('');
    setNewBudgetAmount('');
    setNewBudgetYear(new Date().getFullYear().toString());
    setNewBudgetMonth([]);
  };

  const handleEditBudget = (index) => {
    const budgetToEdit = budgets[index];
    setNewBudgetCategory(budgetToEdit.category);
    setNewBudgetYear(budgetToEdit.year ? budgetToEdit.year.toString() : new Date().getFullYear().toString());
    setNewBudgetMonth(budgetToEdit.months ? budgetToEdit.months.map(String) : []);
    setNewBudgetAmount(budgetToEdit.amount.toString());
    setEditingBudgetIndex(index);
  };

  const _deleteBudget = (index) => {
    const updatedBudgets = budgets.filter((_, i) => i !== index);
    setBudgets(updatedBudgets);
    setShowConfirmModal(false);
  };

  const handleDeleteBudget = (index) => {
    const budgetToDelete = budgets[index];
    setConfirmModalTitle('Confirm Budget Deletion');
    setConfirmModalMessage(`Are you sure you want to delete the budget for "${budgetToDelete.category}" for ${budgetToDelete.months && budgetToDelete.months.length > 0 ? budgetToDelete.months.map(monthNum => formatToLocalTime(new Date(0, monthNum - 1).toISOString(), monthLongOptions)).join(', ') + ' ' : ''}${budgetToDelete.year} with amount ${budgetToDelete.amount.toFixed(2)}? This action cannot be undone.`);
    setConfirmModalAction(() => () => _deleteBudget(index));
    setShowConfirmModal(true);
  };

  const handleCopyBudgets = () => {
    if (!sourceCopyYear || !targetCopyYear) {
      alert('Please select both source and target years.');
      return;
    }
    if (sourceCopyYear === targetCopyYear) {
      alert('Source and target years cannot be the same.');
      return;
    }

    const budgetsToCopy = budgets.filter(budget => budget.year.toString() === sourceCopyYear);

    if (budgetsToCopy.length === 0) {
      alert(`No budgets found for the year ${sourceCopyYear} to copy.`);
      return;
    }

    let newBudgets = [...budgets];
    let copiedCount = 0;
    let skippedCount = 0;

    budgetsToCopy.forEach(budget => {
      const newBudget = { ...budget, year: parseInt(targetCopyYear) };
      const budgetsForValidation = newBudgets.filter(b => b.year.toString() !== targetCopyYear);

      if (isBudgetOverlap(newBudget, budgetsForValidation)) {
        console.warn(`Skipping budget for ${newBudget.category} in ${newBudget.year} due to overlap.`);
        skippedCount++;
      } else {
        newBudgets.push(newBudget);
        copiedCount++;
      }
    });

    setBudgets(newBudgets);
    alert(`Successfully copied ${copiedCount} budgets from ${sourceCopyYear} to ${targetCopyYear}. ${skippedCount > 0 ? `Skipped ${skippedCount} budgets due to overlaps.` : ''}`);
  };

  const availableCategories = [{ name: "ALL_CATEGORIES", subcategories: [] }, ...categories];

  const handleIconSelect = (icon) => {
    if (contextForIconPicker.type === 'newCategory') {
        setSelectedIcon(icon);
    } else if (contextForIconPicker.type === 'editCategory') {
        setCurrentCategoryEdit(prev => ({ ...prev, icon: icon }));
    } else if (contextForIconPicker.type === 'newSubcategory') {
        setSubcategoryIcons(prev => ({ ...prev, [contextForIconPicker.categoryIndex]: icon }));
    }
    setShowIconPickerModal(false);
  };

  const monthLongOptions = { month: 'long' };
  const monthShortOptions = { month: 'short' };

  return (
    <div className="settings-container">
        {profileType === 'EXPENSE_MANAGER' ? (
            <>
                <Tabs activeKey={settingsSubTabKey} onSelect={(k) => setSettingsSubTabKey(k)} className="mb-4 nav-tabs-bold">
                    <Tab eventKey="categories" title={<><Tags className="me-2" />Categories</>}>
                        <Card className="settings-card">
                            <Card.Header>Manage Categories and Subcategories</Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={8}>
                                        {categories.length > 0 ? (
                                            categories.map((category, index) => (
                                                <Card key={index} className="mb-3 category-card">
                                                    <Card.Header onClick={() => toggleSubcategories(index)} style={{ cursor: 'pointer' }} className="d-flex justify-content-between align-items-center">
                                                        <div className="d-flex align-items-center">
                                                            {renderIcon(category.icon)}
                                                            <strong>{category.name}</strong>
                                                        </div>
                                                        <div>
                                                            <Button variant="outline-light" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleEditCategoryClick(index); }}><Pencil /></Button>
                                                            <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteCategoryClick(index); }}><Trash /></Button>
                                                            <span className="ms-2">{openSubcategories[index] ? <ChevronUp /> : <ChevronDown />}</span>
                                                        </div>
                                                    </Card.Header>
                                                    <Collapse in={openSubcategories[index]}>
                                                        <div>
                                                            <Card.Body>
                                                                <ListGroup variant="flush">
                                                                    {category.subcategories.map((subcategory, subIndex) => {
                                                                        const subcategoryName = typeof subcategory === 'string' ? subcategory : subcategory.name;
                                                                        const subcategoryIcon = typeof subcategory === 'object' ? subcategory.icon : null;
                                                                        return (
                                                                            <ListGroup.Item key={subIndex} className="d-flex justify-content-between align-items-center">
                                                                                <div className="d-flex align-items-center">
                                                                                    {renderIcon(subcategoryIcon)}
                                                                                    <span>{subcategoryName}</span>
                                                                                </div>
                                                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteSubcategoryClick(index, subIndex)}><Trash /></Button>
                                                                            </ListGroup.Item>
                                                                        );
                                                                    })}
                                                                </ListGroup>
                                                                <InputGroup className="mt-3">
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={newSubcategoryInputs[index] || ''}
                                                                        onChange={(e) => setNewSubcategoryInputs(prev => ({ ...prev, [index]: e.target.value }))}
                                                                        placeholder="New subcategory name"
                                                                    />
                                                                    <Button 
                                                                      variant="outline-secondary" 
                                                                      onClick={() => { 
                                                                        setShowIconPickerModal(true); 
                                                                        setContextForIconPicker({ type: 'newSubcategory', categoryIndex: index }); 
                                                                      }}
                                                                    >
                                                                      {renderIcon(subcategoryIcons[index]) || '🏷️'}
                                                                    </Button>
                                                                    <Button variant="success" onClick={() => handleAddSubcategory(index)}><Plus /></Button>
                                                                </InputGroup>
                                                            </Card.Body>
                                                        </div>
                                                    </Collapse>
                                                </Card>
                                            ))
                                        ) : (
                                            <Alert variant="info">No categories defined yet. Add one to get started!</Alert>
                                        )}
                                    </Col>
                                    <Col md={4}>
                                        <Card>
                                            <Card.Header>Add New Category</Card.Header>
                                            <Card.Body>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={newCategory}
                                                        onChange={(e) => setNewCategory(e.target.value)}
                                                        placeholder="Category name"
                                                    />
                                                    <Button variant="outline-secondary" onClick={() => { setShowIconPickerModal(true); setContextForIconPicker({ type: 'newCategory', index: null }); setSelectedIcon(''); }}>
                                                        {renderIcon(selectedIcon) || 'Icon'}
                                                    </Button>
                                                    <Button variant="primary" onClick={handleAddCategory}>
                                                        <Plus />
                                                    </Button>
                                                </InputGroup>
                                                {selectedIcon && (
                                                    <div className="d-flex align-items-center mt-2">
                                                        Selected Icon: <span className="ms-2">{selectedIcon}</span>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    <Tab eventKey="budgets" title={<><PiggyBank className="me-2" />Budgets</>}>
                        <Card className="settings-card">
                            <Card.Header>Manage Budgets</Card.Header>
                            <Card.Body>
                                <Card className="budget-form-card mb-4">
                                    <Card.Header>{editingBudgetIndex !== null ? 'Edit Budget' : 'Add New Budget'}</Card.Header>
                                    <Card.Body>
                                        <Form>
                                            <Row className="mb-3 g-3">
                                                <Col md={3}>
                                                    <Form.Group controlId="formBudgetCategory" className="form-floating">
                                                        <Form.Select
                                                            value={newBudgetCategory}
                                                            onChange={(e) => setNewBudgetCategory(e.target.value)}
                                                        >
                                                            <option value="">Select Category</option>
                                                            {availableCategories.map((cat, index) => (
                                                                <option key={index} value={cat.name}>{cat.name}</option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Label>Category</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group controlId="formBudgetYear" className="form-floating">
                                                        <Form.Select
                                                            value={newBudgetYear}
                                                            onChange={(e) => setNewBudgetYear(e.target.value)}
                                                        >
                                                            {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                                                                <option key={year} value={year}>{year}</option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Label>Year</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group controlId="formBudgetMonth" className="form-floating">
                                                        <Form.Select
                                                            multiple
                                                            value={newBudgetMonth}
                                                            onChange={(e) => {
                                                                const selectedOptions = Array.from(e.target.selectedOptions);
                                                                const selectedMonths = selectedOptions
                                                                    .filter(option => option.value !== "")
                                                                    .map(option => parseInt(option.value));
                                                                setNewBudgetMonth(selectedMonths);
                                                            }}
                                                            className="month-select-multiple"
                                                        >
                                                            <option value="">All Months</option>
                                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(monthNum => (
                                                                <option key={monthNum} value={monthNum}>{formatToLocalTime(new Date(0, monthNum - 1).toISOString(), monthLongOptions)}</option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Label>Months</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group controlId="formBudgetAmount" className="form-floating">
                                                        <Form.Control
                                                            type="number"
                                                            placeholder="Budget amount"
                                                            value={newBudgetAmount}
                                                            onChange={(e) => setNewBudgetAmount(e.target.value)}
                                                            min="0.01"
                                                            step="0.01"
                                                        />
                                                        <Form.Label>Amount</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Button variant={editingBudgetIndex !== null ? "warning" : "success"} onClick={handleAddUpdateBudget}>
                                                {editingBudgetIndex !== null ? <><Pencil className="me-2" />Update Budget</> : <><Plus className="me-2" />Add Budget</>}
                                            </Button>
                                            {editingBudgetIndex !== null && (
                                                <Button variant="secondary" className="ms-2" onClick={() => {
                                                    setEditingBudgetIndex(null);
                                                    setNewBudgetCategory('');
                                                    setNewBudgetAmount('');
                                                    setNewBudgetYear(new Date().getFullYear().toString());
                                                    setNewBudgetMonth([]);
                                                }}>
                                                    Cancel Edit
                                                </Button>
                                            )}
                                        </Form>
                                    </Card.Body>
                                </Card>
                                <Card className="copy-budget-card mb-4">
                                    <Card.Header>Copy Budgets to Another Year</Card.Header>
                                    <Card.Body>
                                        <Row className="mb-3 g-3 align-items-end">
                                            <Col md={5}>
                                                <Form.Group controlId="sourceCopyYear" className="form-floating">
                                                    <Form.Select
                                                        value={sourceCopyYear}
                                                        onChange={(e) => setSourceCopyYear(e.target.value)}
                                                    >
                                                        {[...new Set(budgets.map(b => b.year))].sort((a, b) => a - b).map(year => (
                                                            <option key={`source-${year}`} value={year}>{year}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Label>Source Year</Form.Label>
                                                </Form.Group>
                                            </Col>
                                            <Col md={2} className="d-flex align-items-center justify-content-center">
                                                <ArrowRight size={24} />
                                            </Col>
                                            <Col md={5}>
                                                <Form.Group controlId="targetCopyYear" className="form-floating">
                                                    <Form.Select
                                                        value={targetCopyYear}
                                                        onChange={(e) => setTargetCopyYear(e.target.value)}
                                                    >
                                                        {[...new Set([...budgets.map(b => b.year), new Date().getFullYear(), new Date().getFullYear() + 1])].sort((a, b) => a - b).map(year => (
                                                            <option key={`target-${year}`} value={year}>{year}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Label>Target Year</Form.Label>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Button variant="primary" onClick={handleCopyBudgets}>Copy Budgets</Button>
                                    </Card.Body>
                                </Card>
                                <Card className="budget-list-card mb-4">
                                    <Card.Header>Current Budgets</Card.Header>
                                    <Card.Body>
                                        {budgets.length > 0 ? (
                                            <>
                                                <Form.Group controlId="budgetYearFilter" className="mb-3 form-floating">
                                                    <Form.Select
                                                        value={selectedBudgetYearFilter}
                                                        onChange={(e) => setSelectedBudgetYearFilter(e.target.value)}
                                                    >
                                                        <option value="All">All Years</option>
                                                        {[...new Set(budgets.map(b => b.year))].sort((a, b) => a - b).map(year => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Label>Filter by Year</Form.Label>
                                                </Form.Group>
                                                <Table striped bordered hover responsive className="mt-3 expense-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Category</th>
                                                            <th>Year</th>
                                                            <th>Months</th>
                                                            <th>Amount</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[...budgets]
                                                            .filter(budget => selectedBudgetYearFilter === 'All' || budget.year.toString() === selectedBudgetYearFilter)
                                                            .sort((a, b) => {
                                                                const yearA = a.year || Infinity;
                                                                const yearB = b.year || Infinity;
                                                                if (yearA !== yearB) {
                                                                    return yearB - yearA;
                                                                }

                                                                const monthsA = a.months && a.months.length > 0 ? Math.min(...a.months) : -1;
                                                                const monthsB = b.months && b.months.length > 0 ? Math.min(...b.months) : -1;
                                                                return monthsA - monthsB;
                                                            })
                                                            .map((budget, index) => {
                                                                const originalIndex = budgets.indexOf(budget);
                                                                return (
                                                                    <tr key={originalIndex}>
                                                                        <td>{budget.category}</td>
                                                                        <td>{budget.year}</td>
                                                                        <td>{budget.months && budget.months.length > 0 ? budget.months.map(monthNum => formatToLocalTime(new Date(0, monthNum - 1).toISOString(), monthShortOptions)).join(', ') : 'All'}</td>
                                                                        <td>{formatCurrency(budget.amount, currentCurrency)}</td>
                                                                        <td>
                                                                            <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEditBudget(originalIndex)}>Edit</Button>
                                                                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteBudget(originalIndex)}>Delete</Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </Table>
                                            </>
                                        ) : (
                                            <Alert variant="info" className="mt-3">No budgets configured yet. Add your first budget above!</Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Card.Body>
                        </Card>
                    </Tab>

                    <Tab eventKey="paymentSources" title={<><CreditCard className="me-2" />Payment Sources</>}>
                        <Card className="settings-card">
                            <Card.Header>Manage Payment Sources</Card.Header>
                            <Card.Body>
                                <PaymentSourceManager profileId={profileId} />
                            </Card.Body>
                        </Card>
                    </Tab>
                </Tabs>
                <div className="mt-4 text-center">
                    <Button variant="primary" size="lg" onClick={handleSave}>Save All Settings</Button>
                </div>
            </>
        ) : profileType === 'ASSET_MANAGER' ? (
            <Tabs defaultActiveKey="assetTypes" className="mb-4 nav-tabs-bold">
                <Tab eventKey="assetTypes" title={<><Building className="me-2" />Asset Types</>}>
                    <AssetTypeManager profileId={profileId} />
                </Tab>
            </Tabs>
        ) : null}

        {/* Modals and other components */}
        <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} className="settings-modal">
            <Modal.Header closeButton>
                <Modal.Title>Edit Category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Category Name</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            value={currentCategoryEdit.name}
                            onChange={(e) => setCurrentCategoryEdit({ ...currentCategoryEdit, name: e.target.value })}
                        />
                        <Button variant="outline-secondary" onClick={() => { 
                            setShowIconPickerModal(true); 
                            setContextForIconPicker({ type: 'editCategory', index: currentCategoryEdit.index }); 
                        }}>
                            {renderIcon(currentCategoryEdit.icon) || 'Icon'}
                        </Button>
                    </InputGroup>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleSaveCategoryModal}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Confirmation Modal */}
        <ConfirmationModal
            show={showConfirmModal}
            title={confirmModalTitle}
            message={confirmModalMessage}
            onConfirm={confirmModalAction}
            onCancel={() => setShowConfirmModal(false)}
        />

        <IconPicker
            show={showIconPickerModal}
            handleClose={() => setShowIconPickerModal(false)}
            onSelectIcon={handleIconSelect}
        />
    </div>
  );
};

export default Settings;
